import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Pencil } from "lucide-react";
import { ConfirmDelete } from "@/components/shared";
import { toast } from "sonner";

export const DEFAULT_WA_TEMPLATE = `Assalamualaikum Warahmatullahi Wabarakatuh.

Kepada {nama}

Dengan penuh suka cita, kami mengundang teman-teman yang kami cintai untuk turut serta dalam momen istimewa dalam hidup kami.

Kami, {pasangan}, inshaAllah akan mengikat janji suci dalam ikatan pernikahan.

Acara ini akan berlangsung pada:

📅 : {tanggal}
🕒 : 09.00 WIB
📍 : {lokasi}

Kami sangat berharap kehadiran dan doa restu dari sahabat-sahabat semua dalam merayakan kebahagiaan kami. Kehadiranmu adalah anugerah yang tak ternilai.

Buka undangan digital kami:
{link_undangan}

Dengan rasa syukur,
{pasangan}`;

const VARS = [
  { key: "{nama}", desc: "Nama tamu" },
  { key: "{pasangan}", desc: "Nama pasangan (Profile)" },
  { key: "{tanggal}", desc: "Tanggal pernikahan" },
  { key: "{lokasi}", desc: "Lokasi undangan" },
  { key: "{link_undangan}", desc: "Link undangan personal" },
];

export function renderTemplate(tpl, ctx) {
  if (!tpl) return "";
  return VARS.reduce((s, v) => s.replaceAll(v.key, ctx[v.key.slice(1, -1)] ?? ""), tpl);
}

export default function WATemplateDialog({ open, onOpenChange, templates, onCreate, onUpdate, onRemove }) {
  const [mode, setMode] = useState("list"); // list | form
  const [editing, setEditing] = useState(null);
  const [nama, setNama] = useState("");
  const [isi, setIsi] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setMode("list"); setEditing(null); setNama(""); setIsi(""); }
  }, [open]);

  const openNew = () => { setEditing(null); setNama(""); setIsi(DEFAULT_WA_TEMPLATE); setMode("form"); };
  const openEdit = (t) => { setEditing(t); setNama(t.nama || ""); setIsi(t.isi || ""); setMode("form"); };
  const cancel = () => { setMode("list"); setEditing(null); };

  const insertVar = (v) => {
    setIsi((prev) => (prev || "") + v);
  };

  const save = async () => {
    if (!nama.trim()) { toast.error("Nama template wajib diisi"); return; }
    if (!isi.trim()) { toast.error("Isi undangan wajib diisi"); return; }
    setSaving(true);
    try {
      if (editing) { await onUpdate(editing.id, { nama: nama.trim(), isi }); toast.success("Template diperbarui"); }
      else { await onCreate({ nama: nama.trim(), isi }); toast.success("Template disimpan"); }
      setMode("list");
    } catch { toast.error("Gagal menyimpan template"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="wa-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {mode === "list" ? "Kelola Template WhatsApp" : (editing ? "Edit Template" : "Template Baru")}
          </DialogTitle>
          <DialogDescription>
            {mode === "list"
              ? "Template pesan untuk mengundang tamu via WhatsApp. Gunakan variabel supaya isi tersubstitusi otomatis."
              : "Isi pesan yang akan dikirim. Klik variabel di samping untuk menyisipkan otomatis."}
          </DialogDescription>
        </DialogHeader>

        {mode === "list" ? (
          <div className="space-y-3 py-2">
            {templates.length === 0 ? (
              <p className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
                Belum ada template. Klik "Tambah Template" untuk membuat template pertama Anda.
              </p>
            ) : (
              templates.map((t) => (
                <div key={t.id} data-testid={`wa-template-row-${t.id}`} className="flex items-start justify-between gap-3 rounded-xl border border-border p-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{t.nama}</p>
                    <p className="mt-1 line-clamp-3 whitespace-pre-line text-xs text-muted-foreground">{t.isi}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button data-testid={`wa-template-edit-${t.id}`} onClick={() => openEdit(t)} size="icon" variant="ghost" className="h-8 w-8">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <ConfirmDelete
                      testId={`wa-template-delete-${t.id}`}
                      onConfirm={() => onRemove(t.id)}
                      trigger={
                        <Button data-testid={`wa-template-delete-${t.id}`} size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      }
                    />
                  </div>
                </div>
              ))
            )}
            <Button data-testid="wa-template-new" onClick={openNew} className="gap-2 rounded-full">
              <Plus className="h-4 w-4" /> Tambah Template
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 py-2 md:grid-cols-[1fr_180px]">
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nama Template</Label>
                <Input data-testid="wa-template-nama" value={nama} onChange={(e) => setNama(e.target.value)} placeholder="mis. Formal, Santai, Reminder" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Isi Undangan</Label>
                <Textarea data-testid="wa-template-isi" value={isi} onChange={(e) => setIsi(e.target.value)} rows={16} className="mt-1.5 font-mono text-sm" />
              </div>
              <div className="flex gap-2">
                <Button data-testid="wa-template-save" onClick={save} disabled={saving}>
                  {saving ? "Menyimpan..." : "Simpan"}
                </Button>
                <Button data-testid="wa-template-cancel" onClick={cancel} variant="outline">
                  Batal
                </Button>
              </div>
            </div>
            <div className="space-y-2 rounded-xl border border-border bg-muted/30 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Variabel</p>
              <p className="text-[11px] text-muted-foreground">Klik untuk menyisipkan ke isi undangan.</p>
              <div className="space-y-1.5">
                {VARS.map((v) => (
                  <button key={v.key} type="button" data-testid={`wa-var-${v.key.replace(/[{}]/g, "")}`} onClick={() => insertVar(v.key)}
                    className="block w-full rounded-lg border border-border bg-background px-2.5 py-2 text-left transition-colors hover:border-primary hover:bg-primary/5">
                    <Badge variant="outline" className="font-mono text-[10px]">{v.key}</Badge>
                    <p className="mt-1 text-[10px] text-muted-foreground">{v.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
